/** A value supplied by Firestore Timestamp without coupling date helpers to Firebase. */
export interface DateLikeTimestamp {
  toDate(): Date;
}

export type LocalDateInput = Date | DateLikeTimestamp;

const LOCAL_DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

/** Converts an instant to the user's local calendar day, never UTC. */
export function toLocalDateKey(value: LocalDateInput = new Date()): string {
  const date = value instanceof Date ? value : value.toDate();
  if (Number.isNaN(date.getTime())) {
    throw new Error('Invalid date');
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** True only for a real Gregorian calendar date in YYYY-MM-DD form. */
export function isLocalDateKey(value: string): boolean {
  if (!LOCAL_DATE_KEY.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  return (
    parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day
  );
}

export function assertLocalDateKey(value: string, field = 'date'): void {
  if (!isLocalDateKey(value)) {
    throw new Error(`${field} must be a valid local date in YYYY-MM-DD format`);
  }
}

/** Local-date keys sort lexicographically in chronological order. */
export function isDateInInclusiveRange(
  date: string,
  startDate: string,
  endDate: string
): boolean {
  assertLocalDateKey(date, 'date');
  assertLocalDateKey(startDate, 'startDate');
  assertLocalDateKey(endDate, 'endDate');
  if (startDate > endDate) throw new Error('startDate must be on or before endDate');
  return startDate <= date && date <= endDate;
}

/** Inclusive ranges overlap when they share even their boundary date. */
export function doLocalDateRangesOverlap(
  firstStart: string,
  firstEnd: string,
  secondStart: string,
  secondEnd: string
): boolean {
  assertLocalDateKey(firstStart, 'firstStart');
  assertLocalDateKey(firstEnd, 'firstEnd');
  assertLocalDateKey(secondStart, 'secondStart');
  assertLocalDateKey(secondEnd, 'secondEnd');
  if (firstStart > firstEnd || secondStart > secondEnd) {
    throw new Error('A date range start must be on or before its end');
  }
  return firstStart <= secondEnd && secondStart <= firstEnd;
}
